<template>
  <figure
    class="image-element"
    :class="{
      'image-element--load-error': isError
    }"
  >
    <picture
      v-if="imgSrcMobile && imgSrcDesktop && imgSrc"
      class="image-element__pic"
      :class="pictureClass"
    >
      <template
        v-if="loadWebp"
      >
        <source
          :srcset="getWebpSrc(imgSrcMobile)"
          :media="`(max-width: ${breakpoint}px)`"
          type="image/webp"
        >
        <source
          :srcset="getWebpSrc(imgSrcDesktop)"
          :media="`(min-width: ${breakpoint}px)`"
          type="image/webp"
        >
      </template>
      <source
        :srcset="getWebpFallbackSrc(imgSrcMobile)"
        :media="`(max-width: ${breakpoint}px)`"
      >
      <source
        :srcset="getWebpFallbackSrc(imgSrcDesktop)"
        :media="`(min-width: ${breakpoint}px)`"
      >
      <img
        class="image-element__img"
        :class="imgClass"
        :src="imgSrc"
        :alt="alt"
        :width="width"
        :height="height"
        :loading="loading"
        :fetchpriority="fetchpriority"
        @error="errorHandler"
      >
    </picture>
    <picture
      v-else-if="!imgSrcMobile && imgSrcDesktop && imgSrc"
      class="image-element__pic"
      :class="pictureClass"
    >
      <template
        v-if="loadWebp"
      >
        <source
          :srcset="getWebpSrc(imgSrcDesktop)"
          type="image/webp"
        >
      </template>
      <source
        :srcset="getWebpFallbackSrc(imgSrcDesktop)"
      >
      <img
        class="image-element__img"
        :class="imgClass"
        :src="imgSrc"
        :alt="alt"
        :width="width"
        :height="height"
        :loading="loading"
        :fetchpriority="fetchpriority"
        @error="errorHandler"
      >
    </picture>
    <picture
      v-else-if="loadWebp"
    >
      <source
        :srcset="getWebpSrc(imgSrc)"
        type="image/webp"
      >
      <source
        :srcset="getWebpFallbackSrc(imgSrc)"
        media="(min-width: 1px)"
      >
      <img
        class="image-element__img"
        :class="imgClass"
        :src="imgSrc"
        :alt="alt"
        :width="width"
        :height="height"
        :loading="loading"
        :fetchpriority="fetchpriority"
        @error="errorHandler"
      >
    </picture>
    <img
      v-else
      class="image-element__img"
      :class="imgClass"
      :src="imgSrc"
      :alt="alt"
      :width="width"
      :height="height"
      :loading="loading"
      @error="errorHandler"
    >
    <figcaption
      v-if="aiLabelsList && aiLabelsList.length"
      class="image-element__ai-labels | image-ai-labels"
    >
      <span
        v-for="(label, index) in aiLabelsList"
        :key="index"
        class="image-element__ai-labels | image-ai-labels__item"
        :class="`image-ai-labels__item--${label}`"
      >
        {{ t(`general.ai.image_${label}`) }}
      </span>
    </figcaption>
  </figure>
</template>

<script setup lang="ts">
  import {computed, ref, watch} from 'vue';
  import jsGlobalConfig from '../../globals/jsGlobalConfig';
  import diContainer from '../../utilities/js/diContainer/diContainer';
  import type {TransformerPipeline} from '../../utilities/js/imageUrlTransformer/TransformerPipeline';
  import {t} from '../../utilities/js/translations/translations';

  interface ImageElementProps {
    src: string[];
    srcMobile?: string[];
    alt?: string;
    breakpoint?: number;
    width?: number|string;
    height?: number|string;
    pictureClass?: string;
    imgClass?: string;
    fallbackUrl?: string;
    webp?: boolean;
    loading?: 'lazy' | 'eager';
    fetchpriority?: string | null;
    aiLabels?: Array<'generated' | 'modified'>;
  }

  const props = withDefaults(defineProps<ImageElementProps>(), {
    alt: null,
    breakpoint: 767,
    width: 200,
    height: 100,
    pictureClass: '',
    imgClass: '',
    fallbackUrl: '',
    webp: false,
    loading: 'lazy',
    fetchpriority: null,
    srcMobile: null,
    aiLabels: null,
  });

  const isError = ref(false);
  const imgSrc = ref('');
  const imgSrcMobile = ref('');
  const imgSrcDesktop = ref('');

  const productImgUrlPattern = /\/{2}[^/]+\/[^/]+\/[\w,\s-]+\.(png|jpg)/g;
  const imageUrlTransformer: TransformerPipeline = diContainer.get('imageUrlTransformer');

  const getFullSrcString = (srcArray: string[]) => {
    if (!srcArray || srcArray.length < 1) {
      return '';
    }

    const src = [];
    for (let i = 0, l = srcArray.length; i < l; i += 1) {
      src.push(`${imageUrlTransformer.execute(srcArray[i])} ${i + 1}x`);
    }
    return src.join(', ');
  };

  const errorHandler = () => {
    const fallback = props.fallbackUrl || jsGlobalConfig().getFallbackImage();
    imgSrc.value = fallback;
    if (imgSrcMobile.value) {
      imgSrcMobile.value = fallback;
    }
    if (imgSrcDesktop.value) {
      imgSrcDesktop.value = fallback;
    }
    isError.value = true;
  };

  const setImageUrls = () => {
    imgSrc.value = props.src ? imageUrlTransformer.execute(props.src[0]) : '';
    imgSrcMobile.value = props.srcMobile ? getFullSrcString(props.srcMobile) : '';
    imgSrcDesktop.value = ((props.src && props.src.length > 1) || (props.src && imgSrcMobile.value)) ? getFullSrcString(props.src) : '';

    if (!imgSrc.value) {
      errorHandler();
    }
  };

  const isWebpUrl = computed(() => !!imgSrc.value.match(/\.webp$/gi));

  const loadWebp = computed(() => isWebpUrl.value || !!imgSrc.value.match(/\.(png|jpg)$/) && props.webp);

  const getWebpSrc = (src: string) => {
    if (isWebpUrl.value) {
      return src;
    }

    let result = src;
    const urlMatches = src.match(productImgUrlPattern);
    if (urlMatches && urlMatches.length) {
      urlMatches.forEach((url) => {
        const srcArray = url.split('/');
        if (srcArray[3].includes('_90')) {
          srcArray[3] = srcArray[3].includes('retina')
            ? srcArray[3].replace('_retina_90', '_transparent_retina_webp')
            : srcArray[3].replace('_90', '_transparent_webp');
        } else {
          srcArray[3] += '_webp';
        }
        result = result.replace(url, srcArray.join('/'));
      });
      result = result.replace(/(\.jpg|\.png)/g, '.webp');
    } else {
      // eslint-disable-next-line no-console
      console.warn(`Image Source mismatch: ${src}`);
    }

    return result;
  };

  const getWebpFallbackSrc = (src: string) => {
    const fallbackFormat = imgSrc.value.match(/\.png$/) ? 'png' : 'jpg';
    return src
      .replace('/master/', `/bo_${fallbackFormat}/`)
      .replace('.webp', `.${fallbackFormat}`);
  };

  watch([
    () => props.src,
    () => props.srcMobile
  ], setImageUrls, {immediate: true});

  const aiLabelsList = computed(() => {
    return props.aiLabels?.filter(label => !!t(`general.ai.image_${label}`))
  })
</script>
